import {
  IExpressionQueryFieldsFragment,
  Maybe,
  RetentionCohortOperatorKey,
  SimpleCohortTypeKey,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { ICohortRow, RowType } from '../../../../redux/slices/oldBuilder';
import { AppDispatch } from '../../../../redux/store';
import {
  ICachedCohortEntity,
  setCachedCohort,
} from '../../../../redux/slices/cachedEntities';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import { batch } from 'react-redux';
import { formatTimeUnit, renderBracketRange } from '../../../../utils';
import { IParsedAliasReplacements } from '../../../Chart/interfaces';
import { getAliasedName } from '../../../../utils/redux/applyAliasReplacements';

const ALLOWED_CONDITION_TYPES = [
  SimpleCohortTypeKey.Expression,
  SimpleCohortTypeKey.Measure,
];

// in retention cohort, of operator is in below list, and the retention is bracket time unit
// we need to return custom period, but not single day/week/month/etc
export const RETENTION_CUSTOM_BRACKET_OPERATOR_TYPES = [
  RetentionCohortOperatorKey.RetainedOn,
  RetentionCohortOperatorKey.NotRetainedOn,
  RetentionCohortOperatorKey.RetainedSince,
  RetentionCohortOperatorKey.NotRetainedSince,
];
export const useShowCohortPercentileFilter = (
  cohortRow: ICohortRow,
): boolean => {
  const { cohortId, simpleCohortTypeKey, isExpanded } = cohortRow;

  return Boolean(
    (!cohortId || isExpanded) &&
      ALLOWED_CONDITION_TYPES.includes(simpleCohortTypeKey),
  );
};

export const cohortSampleSaveRowMeta = {
  rowType: RowType.builderCohorts,
  rowIndex: 0,
};

export const saveCachedCohortAndOpenModalThunk =
  (
    cachedCohort: ICachedCohortEntity,
    isExecuteAfterRedirect: boolean,
    modalAnalysisId: string,
  ) =>
  (dispatch: AppDispatch) => {
    batch(() => {
      dispatch(setCachedCohort(cachedCohort));
      if (!isExecuteAfterRedirect) {
        dispatch(
          openModal({
            type: ModalSliceType.SaveCohort,
            data: {
              rowMeta: cohortSampleSaveRowMeta,
            },
            id: modalAnalysisId,
          }),
        );
      }
    });
  };

export const parseRetentionUnit = (unit?: TimeUnit) => {
  const formatted = formatTimeUnit(unit);

  if (unit === TimeUnit.FourWeek) {
    return formatted;
  }

  return `${formatted}(s)`;
};

// In retention custom time brackets, we have a list of number to represent the time brackets.
// this function is a helper to generate a text with specific index.
// For example, [1, 2, 3] => ['day 0 ', 'day 1 - 3, 'day 3 - 6']
// if index is 1: return 'day 1 - 3'
export const generateCustomRetentionTimeBracketText = (
  index: number,
  customTimeBrackets: number[],
  retentionTimeUnit: TimeUnit,
) => {
  const prefix = formatTimeUnit(retentionTimeUnit);
  const range = renderBracketRange(index, customTimeBrackets);
  return [prefix, range].join(' ');
};

// generate measure options for expression cohort measure selector
// also update measure name if alias exists
export const generateExpressionCohortMeasureOptions = (
  measure: IExpressionQueryFieldsFragment['measure'],
  aliasReplacements: Maybe<IParsedAliasReplacements>,
) => {
  if (measure.__typename === 'SimpleMeasure') {
    return [
      {
        value: measure.id,
        text: getAliasedName(
          measure.name,
          measure.userAlias.aliasId,
          aliasReplacements,
        ).name,
      },
    ];
  } else {
    const res = [];
    if (measure.formula) {
      res.push({
        value: measure.id,
        text: getAliasedName(
          measure.name,
          measure.userAlias.aliasId,
          aliasReplacements,
        ).name,
      });
    }
    for (const childMeasure of measure.measures) {
      res.push({
        value: childMeasure.id,
        text: `${childMeasure.varName}: ${
          getAliasedName(
            childMeasure.name,
            childMeasure.userAlias.aliasId,
            aliasReplacements,
          ).name
        }`,
      });
    }
    return res;
  }
};

const COHORTS_WITH_INTEGER_OPERATOR_VALUES = [
  SimpleCohortTypeKey.Performed,
  SimpleCohortTypeKey.Retention,
];

export const getCohortOperatorValuePrecision = (
  cohortTypeKey: SimpleCohortTypeKey,
): number => {
  if (COHORTS_WITH_INTEGER_OPERATOR_VALUES.includes(cohortTypeKey)) {
    return 0;
  }
  return 2;
};
